// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The point-level answer. A region can look competitive and still leave you
// with one bar, because an SA3 is enormous — so this measures from an actual
// spot: how far is each carrier's nearest tower, and how far is its nearest
// tower that carries spectrum able to reach you.

import type { TowerData } from '../types';
import { buildSiteGrid, nearestSite, bandsOf, type SiteGrid } from '../data';
import { CARRIER_ALIAS, CARRIER_COLOR } from '../colors';
import { attr, esc, km, num } from '../format';
import { gloss } from '../glossary';
import { openSite } from '../drawer';
import { viewHead, onActivate } from './ui';

// Rules of thumb for how far each spectrum class realistically carries in open
// country. Stated as guides on the ruler, and labelled as approximations.
const REACH_GUIDE = 15;
const CAPACITY_GUIDE = 3;
const MAX_KM = 60;

interface Pick {
  name: string;
  lat: number;
  lon: number;
  /** Set when the point is a region's tower centroid rather than a real place. */
  areaKm2?: number;
}

export function mount(el: HTMLElement, data: TowerData): () => void {
  const grid: SiteGrid = buildSiteGrid(data.sites);
  // Region centroids give a searchable starting point without shipping another
  // dataset: the mean of a region's own tower positions is where its people are.
  const places = buildPlaces(data);
  let pick: Pick = places.find((p) => /Sydney Inner City|Melbourne City/.test(p.name)) ?? places[0];

  el.innerHTML = `
    ${viewHead({
      title: 'What reaches this spot',
      question: 'From here, how far away is each network — and how far is each network that can actually reach me?',
      blurb: `Pick a place, or use your own location. For each carrier this measures the nearest ${gloss('site', 'mobile site')} of any kind, and the nearest one carrying ${gloss('lowband', 'long-reach spectrum')}. The gap between those two is the difference between "there is a tower nearby" and "that tower can reach me".`,
    })}
    <div class="picker">
      <label for="place-search">Place</label>
      <input id="place-search" type="search" placeholder="Type a suburb or region…" autocomplete="off" role="combobox" aria-expanded="false" aria-controls="place-list" />
      <ul id="place-list" class="place-list" role="listbox" hidden></ul>
      <button class="btn" data-geo>Use my location</button>
    </div>
    <p class="picked" data-picked></p>
    <div data-ruler></div>
    <p class="src-note">Distances are straight-line (great-circle) from the chosen point to the nearest registered site, ${data.meta.latestYear} snapshot. Real coverage also depends on terrain, buildings, the tower's height and where its antennas point — this is what the register can tell you, not a signal prediction.</p>
  `;

  const input = el.querySelector('#place-search') as HTMLInputElement;
  const list = el.querySelector('#place-list') as HTMLElement;
  const ruler = el.querySelector('[data-ruler]') as HTMLElement;
  const picked = el.querySelector('[data-picked]') as HTMLElement;

  function render(): void {
    // Say plainly what the point IS. A region's tower centroid is not a town,
    // and reporting "Mid West" from one point inside 466,795 km2 would imply a
    // precision the answer does not have.
    const what = pick.areaKm2
      ? ` <span class="muted">— the average position of this region's towers, not a town. The region covers ${num(pick.areaKm2)} km², so a spot at its edge can look very different.</span>`
      : '';
    picked.innerHTML = `Measuring from <strong>${esc(pick.name)}</strong> <span class="muted">(${pick.lat.toFixed(4)}, ${pick.lon.toFixed(4)})</span>${what}`;
    const lanes = data.meta.carriers.map((c, ci) => {
      const any = nearestSite(data.sites, grid, pick.lat, pick.lon, (i) => data.sites.mno[i] === ci);
      const low = nearestSite(
        data.sites,
        grid,
        pick.lat,
        pick.lon,
        (i) => data.sites.mno[i] === ci && (data.sites.mask[i] & data.masks.modernLow) !== 0,
      );
      return { c, ci, any, low };
    });
    ruler.innerHTML = rulerSvg(data, lanes) + verdict(data, lanes);
    ruler.querySelectorAll('[data-site]').forEach((m) => {
      m.setAttribute('tabindex', '0');
      onActivate(m, () => openSite(data, Number(m.getAttribute('data-site'))));
    });
  }

  // Debounced type-ahead over region names.
  let timer: number | undefined;
  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      const q = input.value.trim().toLowerCase();
      if (q.length < 2) {
        list.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        return;
      }
      const hits = places.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 8);
      list.innerHTML = hits.length
        ? hits.map((p, i) => `<li role="option" tabindex="0" data-place="${i}">${esc(p.name)}</li>`).join('')
        : '<li class="none">No matching place</li>';
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      list.querySelectorAll('[data-place]').forEach((li, i) => {
        const choose = () => {
          pick = hits[i];
          input.value = '';
          list.hidden = true;
          input.setAttribute('aria-expanded', 'false');
          render();
        };
        li.addEventListener('click', choose);
        li.addEventListener('keydown', (e) => {
          if ((e as KeyboardEvent).key === 'Enter') choose();
        });
      });
    }, 300);
  });

  el.querySelector('[data-geo]')?.addEventListener('click', () => {
    if (!navigator.geolocation) {
      picked.innerHTML = '<span class="bad">This browser cannot share a location.</span>';
      return;
    }
    picked.textContent = 'Finding your location…';
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        pick = { name: 'Your location', lat: pos.coords.latitude, lon: pos.coords.longitude };
        render();
      },
      () => {
        picked.innerHTML = '<span class="bad">Could not get your location — pick a place instead.</span>';
      },
      { timeout: 8000 },
    );
  });

  // Named so it can be removed: mount() runs again on every visit to this
  // view, and an anonymous document listener would accumulate one per visit.
  const onDocClick = (e: Event) => {
    if (!(e.target as Element).closest?.('.picker')) list.hidden = true;
  };
  document.addEventListener('click', onDocClick);

  render();

  return () => {
    document.removeEventListener('click', onDocClick);
    window.clearTimeout(timer);
  };
}

function buildPlaces(data: TowerData): Pick[] {
  const acc = new Map<number, { lat: number; lon: number; n: number }>();
  const s = data.sites;
  for (let i = 0; i < s.n; i++) {
    const ri = s.sa3[i];
    if (ri < 0) continue;
    const e = acc.get(ri) ?? { lat: 0, lon: 0, n: 0 };
    e.lat += s.lat[i];
    e.lon += s.lon[i];
    e.n++;
    acc.set(ri, e);
  }
  return [...acc.entries()]
    .map(([ri, e]) => ({
      name: `${data.regions[ri].name}, ${data.regions[ri].state}`,
      lat: e.lat / e.n,
      lon: e.lon / e.n,
      areaKm2: data.regions[ri].areaKm2,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

interface Lane {
  c: string;
  ci: number;
  any: { index: number; km: number } | null;
  low: { index: number; km: number } | null;
}

function rulerSvg(data: TowerData, lanes: Lane[]): string {
  const W = 900;
  const laneH = 92;
  const H = 54 + lanes.length * laneH;
  const P = { l: 118, r: 30, t: 44 };
  const iw = W - P.l - P.r;
  // sqrt keeps short distances readable while still showing a 60 km outlier.
  const x = (d: number) => P.l + (Math.sqrt(Math.min(d, MAX_KM)) / Math.sqrt(MAX_KM)) * iw;

  const guides = [
    { d: CAPACITY_GUIDE, label: `${CAPACITY_GUIDE} km` },
    { d: REACH_GUIDE, label: `${REACH_GUIDE} km` },
    { d: 30, label: '30 km' },
    { d: MAX_KM, label: `${MAX_KM} km+` },
  ]
    .map(
      (g) =>
        `<line x1="${x(g.d).toFixed(1)}" x2="${x(g.d).toFixed(1)}" y1="${P.t - 12}" y2="${H - 18}" stroke="var(--border-subtle)" stroke-dasharray="4 4" />
         <text x="${x(g.d).toFixed(1)}" y="${P.t - 18}" class="ax" text-anchor="middle">${g.label}</text>`,
    )
    .join('');

  const rows = lanes
    .map((ln, i) => {
      const c = ln.c as keyof typeof CARRIER_COLOR;
      const y = P.t + i * laneH + 30;
      const col = CARRIER_COLOR[c];
      if (!ln.any) {
        return `<text x="${P.l - 12}" y="${y + 5}" class="lane-name" text-anchor="end" fill="${col}">${esc(CARRIER_ALIAS[c])}</text>
                <text x="${P.l + 10}" y="${y + 5}" class="lane-none">No site of this network found within range</text>`;
      }
      const anyX = x(ln.any.km);
      const lowX = ln.low ? x(ln.low.km) : null;
      const gapBar =
        lowX !== null && lowX > anyX + 1
          ? `<line x1="${anyX.toFixed(1)}" x2="${lowX.toFixed(1)}" y1="${y}" y2="${y}" stroke="${col}" stroke-width="7" opacity="0.22" stroke-linecap="round" />`
          : '';
      const outOfReach = ln.low ? ln.low.km > REACH_GUIDE : true;
      const lowMark =
        ln.low !== null
          ? `<circle cx="${lowX!.toFixed(1)}" cy="${y}" r="9" fill="${col}" stroke="#fff" stroke-width="2"
               class="mark" data-site="${ln.low.index}" role="button" tabindex="0"
               data-tip="${attr(tipFor(data, ln.low.index, ln.low.km, true))}" />`
          : `<text x="${P.l + 10}" y="${y + 22}" class="lane-none">No long-reach tower within ${MAX_KM} km</text>`;
      const label = ln.low
        ? `${km(ln.any.km)} to the nearest tower · ${km(ln.low.km)} to the nearest that reaches far${outOfReach ? ' — beyond typical range' : ''}`
        : `${km(ln.any.km)} to the nearest tower — but none of this network's long-reach spectrum is nearby`;

      return `<text x="${P.l - 12}" y="${y + 5}" class="lane-name" text-anchor="end" fill="${col}">${esc(CARRIER_ALIAS[c])}</text>
        <line x1="${P.l}" x2="${W - P.r}" y1="${y}" y2="${y}" stroke="var(--border-subtle)" />
        ${gapBar}
        <circle cx="${anyX.toFixed(1)}" cy="${y}" r="7.5" fill="#fff" stroke="${col}" stroke-width="2.5"
          class="mark" data-site="${ln.any.index}" role="button" tabindex="0"
          data-tip="${attr(tipFor(data, ln.any.index, ln.any.km, false))}" />
        ${lowMark}
        <text x="${P.l}" y="${y + 34}" class="lane-sub ${outOfReach ? 'warn' : ''}">${esc(label)}</text>`;
    })
    .join('');

  return `<div class="chart ruler-wrap">
    <svg viewBox="0 0 ${W} ${H}" class="ruler" role="img" aria-label="Distance from the chosen point to each carrier's nearest tower">
      ${guides}${rows}
    </svg>
  </div>
  <div class="legend">
    <span class="legend-item"><span class="swatch swatch-ring"></span>Nearest tower of any kind</span>
    <span class="legend-item"><span class="swatch" style="background:#475569"></span>Nearest tower with long-reach spectrum</span>
    <span class="legend-item"><span class="swatch swatch-bar"></span>The gap between them</span>
  </div>`;
}

function tipFor(data: TowerData, i: number, dist: number, low: boolean): string {
  const s = data.sites;
  const carrier = data.meta.carriers[s.mno[i]];
  const bands = bandsOf(data, s.mask[i]);
  const lows = bands.filter((b) => b.cls === 'low').map((b) => b.id);
  return [
    `${CARRIER_ALIAS[carrier]} · site ${s.rfnsa[i] || '—'}`,
    `${km(dist)} away`,
    low ? `Long-reach bands: ${lows.join(', ')}` : `Bands: ${bands.map((b) => b.id).join(', ')}`,
    s.fund[i] >= 0 ? `Publicly co-funded (${s.programs[s.fund[i]]})` : 'Built commercially',
    'Click for full detail',
  ].join('\n');
}

function verdict(data: TowerData, lanes: Lane[]): string {
  const reachable = lanes.filter((l) => l.low && l.low.km <= REACH_GUIDE);
  const names = reachable.map((l) => CARRIER_ALIAS[l.c as keyof typeof CARRIER_ALIAS]);
  let msg: string;
  let tone: string;
  if (reachable.length === 0) {
    const best = lanes.filter((l) => l.low).sort((a, b) => a.low!.km - b.low!.km)[0];
    msg = best
      ? `No network has long-reach spectrum within ${REACH_GUIDE} km of here. The closest is ${CARRIER_ALIAS[best.c as keyof typeof CARRIER_ALIAS]} at ${km(best.low!.km)} — expect patchy service at best.`
      : 'No long-reach tower of any network is anywhere near here.';
    tone = 'alert';
  } else if (reachable.length === 1) {
    msg = `${names[0]} is the only network with long-reach spectrum within ${REACH_GUIDE} km of here. On this evidence it is the only realistic choice.`;
    tone = 'warn';
  } else {
    msg = `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]} all have long-reach spectrum within ${REACH_GUIDE} km — you have a genuine choice here.`;
    tone = 'good';
  }
  const totals = lanes
    .filter((l) => l.any)
    .map((l) => `${CARRIER_ALIAS[l.c as keyof typeof CARRIER_ALIAS]} ${km(l.any!.km)}`)
    .join(' · ');
  return `<div class="verdict verdict-${tone}"><p>${esc(msg)}</p><p class="verdict-sub">Nearest tower of any kind — ${esc(totals)}. Based on ${num(data.sites.n)} registered sites.</p></div>`;
}
